import { ErrorMessage } from '@hookform/error-message';
import ReactButton from 'components/Buttons/ReactButton';
import { ErrorFormMessage } from 'components/ErrorFormMessage';
import FlexSelect from 'components/Inputs/FlexSelect';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Col, Form, Input, Row } from 'reactstrap';
import AddressService from '../../../../../../../services/addressService';

import { brazilianStates } from '../../../../../../../constants/brazil_states';

const EditAddressForm = ({ onAddressUpdated, onCancel, address }) => {
  const { control, errors, setValue, handleSubmit, reset } = useForm();
  const [withoutNumber, setWithoutNumber] = React.useState(false);

  useEffect(() => {
    if (address) {
      setValue('title', address.title || '');
      setValue('zipCode', address.zipCode || '');
      setValue('state', address.state || '');
      setValue('city', address.city || '');
      setValue('neighborhood', address.neighborhood || '');
      setValue('street', address.street || '');
      setValue('complement', address.complement || '');

      const hasNoNumber = !address.number;
      setWithoutNumber(hasNoNumber);
      if (!hasNoNumber) setValue('number', address.number?.toString() || '');
    }
  }, [address, setValue]);

  const onSubmit = async (data) => {
    try {
      const addressData = {
        ...data,
        zipCode: data.zipCode,
        number: withoutNumber ? null : Number(data.number) || null
      };

      await AddressService.updateAddress(address.id, addressData);

      toast.success('Endereço atualizado com sucesso');
      reset();
      onAddressUpdated();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Erro ao atualizar endereço'
      );
    }
  };

  return (
    <Form className="needs-validation" onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col className="mb-3" sm="12">
          <label className="form-control-label text-capitalize">Título*</label>
          <Controller
            render={(props) => (
              <Input
                value={props.value}
                onChange={props.onChange}
                type="text"
                placeholder="Ex: Casa, Trabalho, Endereço principal..."
              />
            )}
            control={control}
            defaultValue=""
            rules={{ required: 'Campo obrigatório' }}
            name="title"
          />
          <ErrorMessage
            errors={errors}
            name="title"
            render={({ message }) => ErrorFormMessage({ message })}
          />
        </Col>
      </Row>

      <Row>
        <Col className="mb-3" sm="4">
          <label className="form-control-label text-capitalize">CEP*</label>
          <Controller
            render={(props) => (
              <Input
                value={props.value}
                onChange={props.onChange}
                type="text"
                placeholder="00000-000"
                maxLength={8}
              />
            )}
            control={control}
            defaultValue=""
            rules={{ required: 'Campo obrigatório' }}
            name="zipCode"
          />
          <ErrorMessage
            errors={errors}
            name="zipCode"
            render={({ message }) => ErrorFormMessage({ message })}
          />
        </Col>

        <Col className="mb-3" sm="4">
          <label className="form-control-label text-capitalize">Estado*</label>
          <Controller
            render={(props) => (
              <FlexSelect
                dataOptions={brazilianStates}
                isMulti={false}
                value={props.value}
                valueController={setValue}
                fieldName="state"
                labelName="name"
                valueName="id"
                isClearable={true}
                closeMenuOnSelect={true}
                placeholder="Selecione o estado"
              />
            )}
            control={control}
            defaultValue=""
            rules={{ required: 'Campo obrigatório' }}
            name="state"
          />
          <ErrorMessage
            errors={errors}
            name="state"
            render={({ message }) => ErrorFormMessage({ message })}
          />
        </Col>

        <Col className="mb-3" sm="4">
          <label className="form-control-label text-capitalize">Cidade*</label>
          <Controller
            render={(props) => (
              <Input
                value={props.value}
                onChange={props.onChange}
                type="text"
                placeholder="Digite a cidade"
              />
            )}
            control={control}
            defaultValue=""
            rules={{ required: 'Campo obrigatório' }}
            name="city"
          />
          <ErrorMessage
            errors={errors}
            name="city"
            render={({ message }) => ErrorFormMessage({ message })}
          />
        </Col>
      </Row>

      <Row>
        <Col className="mb-3" sm="6">
          <label className="form-control-label text-capitalize">Bairro*</label>
          <Controller
            render={(props) => (
              <Input
                value={props.value}
                onChange={props.onChange}
                type="text"
                placeholder="Digite o bairro"
              />
            )}
            control={control}
            defaultValue=""
            rules={{ required: 'Campo obrigatório' }}
            name="neighborhood"
          />
          <ErrorMessage
            errors={errors}
            name="neighborhood"
            render={({ message }) => ErrorFormMessage({ message })}
          />
        </Col>

        <Col className="mb-3" sm="6">
          <label className="form-control-label text-capitalize">Rua</label>
          <Controller
            render={(props) => (
              <Input
                value={props.value}
                onChange={props.onChange}
                type="text"
                placeholder="Digite a rua"
              />
            )}
            control={control}
            defaultValue=""
            name="street"
          />
        </Col>
      </Row>

      <Row>
        <Col className="mb-3" sm="6">
          <label className="form-control-label text-capitalize">Número</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="withoutNumber"
              checked={withoutNumber}
              onChange={(e) => setWithoutNumber(e.target.checked)}
            />
            <label htmlFor="withoutNumber">S/N (sem número)</label>
          </div>
          {!withoutNumber && (
            <Controller
              render={(props) => (
                <Input
                  value={props.value}
                  onChange={props.onChange}
                  type="number"
                  placeholder="Digite o número"
                  min="0"
                />
              )}
              control={control}
              defaultValue=""
              name="number"
            />
          )}
        </Col>

        <Col className="mb-3" sm="6">
          <label className="form-control-label text-capitalize">
            Complemento
          </label>
          <Controller
            render={(props) => (
              <Input
                value={props.value}
                onChange={props.onChange}
                type="text"
                placeholder="Apartamento, bloco, etc."
              />
            )}
            control={control}
            defaultValue=""
            name="complement"
          />
        </Col>
      </Row>

      <Row className="d-flex justify-content-end">
        <Col sm="6" className="text-right">
          <ReactButton
            type="button"
            color="secondary"
            onClick={onCancel}
            style={{ marginRight: '8px' }}
          >
            Cancelar
          </ReactButton>
          <ReactButton type="submit" color="primary">
            Salvar
          </ReactButton>
        </Col>
      </Row>
    </Form>
  );
};

export default EditAddressForm;
